import { z } from 'zod';
import {
  assertRawFilterDepth,
  assertRelationHops,
  type FilterNode,
  filterNodeSchema,
} from './filter-ast';

/**
 * The typed RPC contract for reading records: `POST /api/query`.
 *
 * Phase 1 defines the STRUCTURE only. No compiler, no execution - those arrive
 * with the record layer in Phase 2. Freezing the shape now means the client and
 * the server cannot drift apart while the two halves are built.
 *
 * Everything keys on metadata UUIDs (`objectId`, `fieldId`), never on names
 * (INV-7). Pagination is cursor-based: offset pagination breaks under
 * concurrent writes, which a CRM has constantly.
 */

export type SortDirection = 'asc' | 'desc';
export type NullsPosition = 'first' | 'last';

// `?: T | undefined` rather than `?: T` throughout: under
// exactOptionalPropertyTypes those are different types, and zod's output type
// for `.optional()` is the `| undefined` form.
export interface OrderBy {
  readonly fieldId: string;
  readonly direction: SortDirection;
  readonly nulls?: NullsPosition | undefined;
}

export interface RecordQuery {
  readonly objectId: string;
  /**
   * Field ids to return. Explicit: there is no implicit "select everything".
   *
   * System fields are ordinary field rows (`created_at`, `updated_by`, `position`,
   * ...) and may be selected by id like any other. Filtering and sorting work on the
   * ones that have a user-facing field type; `id`, the actor stamps and `provenance`
   * are jsonb/uuid documents and are read-only for the query surface.
   */
  readonly select: readonly string[];
  readonly filter?: FilterNode | undefined;
  readonly orderBy?: readonly OrderBy[] | undefined;
  readonly limit?: number | undefined;
  /** Opaque cursor from a previous response. */
  readonly cursor?: string | undefined;
  /** Soft-deleted records are hidden unless asked for (the restore view asks). */
  readonly includeDeleted?: boolean | undefined;
  /**
   * Opt-in total. Off by default because it is a second full scan of the filter:
   * a list that only needs "is there a next page" gets that from the cursor.
   */
  readonly withTotalCount?: boolean | undefined;
}

/**
 * A RELATION field resolves to the target's id AND its label, so a list can render
 * a chip without a second request per row. The label follows `label-field.ts`.
 */
export interface RelationValue {
  readonly id: string;
  readonly label: string;
}

export interface QueryRecord {
  readonly id: string;
  /** Selected values by field id. RELATION entries are `RelationValue | null`. */
  readonly values: Readonly<Record<string, unknown>>;
}

export interface RecordQueryResult {
  readonly records: readonly QueryRecord[];
  /** Pass back as `cursor` for the next page. Null when this was the last one. */
  readonly nextCursor: string | null;
  /** Present only when `withTotalCount` was requested. */
  readonly totalCount?: number | undefined;
}

export const RECORD_QUERY_DEFAULT_LIMIT = 50;
export const RECORD_QUERY_MAX_LIMIT = 500;

export const sortDirectionSchema = z.literal(['asc', 'desc']);
export const nullsPositionSchema = z.literal(['first', 'last']);

export const orderBySchema = z.strictObject({
  fieldId: z.uuid(),
  direction: sortDirectionSchema,
  nulls: nullsPositionSchema.optional(),
});

export const recordQuerySchema = z.strictObject({
  objectId: z.uuid(),
  select: z.array(z.uuid()).min(1),
  filter: filterNodeSchema.optional(),
  orderBy: z.array(orderBySchema).optional(),
  limit: z.int().min(1).max(RECORD_QUERY_MAX_LIMIT).optional(),
  cursor: z.string().min(1).optional(),
  includeDeleted: z.boolean().optional(),
  withTotalCount: z.boolean().optional(),
});

/** How many ORDER BY keys a query may carry, before the mandatory id tiebreaker. */
export const RECORD_QUERY_MAX_ORDER_KEYS = 4;

export function parseRecordQuery(input: unknown): RecordQuery {
  /**
   * The depth bound runs BEFORE zod, on the raw body.
   *
   * This schema embeds `filterNodeSchema` directly, so a query never passes through
   * `parseFilterNode`, and one nesting level costs ~44 bytes - roughly 20 000 of them fit inside the
   * 1 MB body limit. Verified: checking after the parse is too late, because the overflow happens
   * inside zod's own `z.lazy` descent and surfaces as a RangeError (a 500) instead of a 400.
   */
  if (typeof input === 'object' && input !== null) {
    const filter = (input as { filter?: unknown }).filter;
    if (filter !== undefined) assertRawFilterDepth(filter);
  }
  const query = recordQuerySchema.parse(input);
  /**
   * D7's second entrance. This schema embeds `filterNodeSchema` directly, so a query never
   * passes through `parseFilterNode` and would otherwise reach the planner with no hop
   * bound at all - which is the trap the research names: a cap enforced at one door is a
   * cap the other door does not have.
   */
  if (query.filter !== undefined) assertRelationHops(query.filter);
  return query;
}

/** Effective page size for a query. */
export function recordQueryLimit(query: RecordQuery): number {
  return query.limit ?? RECORD_QUERY_DEFAULT_LIMIT;
}
